import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { SupportTabExtras, SupportTabs } from '@web/features/help/SupportTabs';
import { BaseLayout, toggleLoadingScreen } from '@web/features/layouts/base';
import { theme } from '@web/features/theme';
import { FaChevronRight } from 'react-icons/fa';

export function mapToId<Data extends { [key: string]: unknown }>(
  obj: Data,
  idx: number,
): Data & { id: number } {
  return { id: idx, ...obj };
}

export default function SupportPage() {
  const router = useRouter();

  const selectedTab = React.useMemo(() => {
    const { tab } = router.query;

    const tabValue = Array.isArray(tab) ? tab[0] : tab;

    const isValidTab = Object.values(SupportTabs).includes(
      tabValue as unknown as SupportTabs,
    );

    if (isValidTab) {
      return tabValue as SupportTabs;
    }

    return SupportTabs.Root;
  }, [router]);

  React.useEffect(() => {
    toggleLoadingScreen(false);
  }, []);

  const goToTab = React.useCallback(
    (tab?: SupportTabs) => {
      return tab === SupportTabs.Root
        ? router.push({ pathname: '/help' })
        : router.push({ pathname: '/help', query: { tab } });
    },
    [router],
  );

  const isRoot = selectedTab === SupportTabs.Root;
  const tabExtras = SupportTabExtras[selectedTab];
  const { title } = tabExtras;

  const Component = React.useMemo(() => {
    if (
      tabExtras &&
      'Component' in tabExtras &&
      typeof tabExtras.Component !== 'undefined'
    ) {
      return tabExtras.Component;
    }

    return null;
  }, [tabExtras]);

  const onMount = React.useMemo(() => {
    if (
      tabExtras &&
      'onMount' in tabExtras &&
      typeof tabExtras.onMount === 'function'
    ) {
      return tabExtras.onMount;
    }

    return null;
  }, [tabExtras]);

  React.useEffect(() => {
    if (onMount) {
      onMount();
    }
  }, [onMount]);

  return (
    <BaseLayout withHomeButton={isRoot} withBackButton={!isRoot}>
      <h1 css={styles.title} className="display">
        {title}
      </h1>

      {selectedTab === 'root' ? (
        <nav css={styles.navContainer}>
          {Object.entries(SupportTabExtras)
            .filter(([tab]) => tab !== SupportTabs.Root)
            .map(([tab, config]) => (
              <button
                key={tab}
                type="button"
                css={styles.navButton}
                className={tab === selectedTab ? 'selected' : ''}
                onClick={() => goToTab(tab as SupportTabs)}
              >
                {config.title}

                {Component ? <FaChevronRight /> : null}
              </button>
            ))}
        </nav>
      ) : null}

      {Component ? (
        <section css={styles.section}>
          <Component />
        </section>
      ) : null}
    </BaseLayout>
  );
}

const styles = {
  title: css`
    font-weight: ${theme.fontWeights.bold};
  `,

  navContainer: css`
    background-color: ${theme.colors.whiteAlpha[300]};
    border-radius: ${theme.radii.md};
  `,

  navButton: css`
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    padding: ${theme.space[4]};
    font-size: ${theme.fontSizes.xl};
    color: ${theme.colors.whiteAlpha[900]};
    font-weight: ${theme.fontWeights.bold};
    background-color: transparent;
    border-color: transparent;

    &:hover,
    &:focus,
    &.shown {
      cursor: pointer;
      text-decoration: underline;
      background: ${theme.colors.whiteAlpha[200]};
      border-radius: ${theme.radii.md};
    }

    &:not(:last-of-type) {
      border-bottom: 1px solid ${theme.colors.whiteAlpha[600]};
    }
  `,

  section: css`
    margin: ${theme.space[4]} 0;
    padding: ${theme.space[4]};
    background-color: ${theme.colors.whiteAlpha[300]};
    border-radius: ${theme.radii.md};

    .counter {
      counter-reset: section;

      li {
        counter-increment: section;
        position: relative;
        margin: ${theme.space[4]} 0;
        display: grid;
        grid-template-areas:
          'icon text'
          'screenshots screenshots';
        align-items: center;
        justify-content: start;

        p {
          grid-area: text;
          line-height: ${theme.lineHeights.tall};
        }

        &:before {
          grid-area: icon;
          align-self: start;
          content: counter(section);
          text-align: center;
          padding-top: 7%;
          height: ${theme.space[6]};
          width: ${theme.space[6]};
          background-color: ${theme.colors.whiteAlpha[900]};
          color: ${theme.colors.purple[800]};
          font-weight: ${theme.fontWeights.semibold};
          border-radius: ${theme.radii.md};
          margin-right: ${theme.space[4]};
          border-radius: ${theme.radii.full};
        }
      }
    }
  `,

  sectionTitle: css`
    margin: ${theme.space[4]} 0;
    font-size: ${theme.fontSizes['3xl']};
    font-weight: ${theme.fontWeights.bold};
  `,

  sectionDescription: css`
    margin: ${theme.space[4]} 0;
    font-size: ${theme.fontSizes.xl};
    font-weight: ${theme.fontWeights.semibold};
  `,
};
